import React, { useState, useEffect, useRef } from 'react';
import { generateLoanAgreement, downloadGeneratedPdf } from '../../services/demoApi';

const Step2DoqSolv = ({ formData, onNext, demoState, setDemoState }) => {
  const [phase, setPhase] = useState('processing'); // 'processing' | 'preview' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const hasCalledApi = useRef(false);

  useEffect(() => {
    if (hasCalledApi.current) return;
    hasCalledApi.current = true;

    const run = async () => {
      try {
        // formData keys now match the template variable keys exactly
        // (populated dynamically from the template schema in Step 1)
        const result = await generateLoanAgreement(formData);

        // Download the raw PDF bytes for later merging
        const pdfBytes = await downloadGeneratedPdf(result.document_id);

        // Store everything in shared state
        setDemoState(prev => ({
          ...prev,
          documentId: result.document_id,
          pdfFileId: result.pdf_file_id,
          templateName: result.template_name,
          agreementPdfBytes: pdfBytes,
        }));

        setPhase('preview');
      } catch (err) {
        console.error('DoqSolv generation failed:', err);
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        setErrorMsg(detail);
        setPhase('error');
      }
    };

    run();
  }, [formData, setDemoState]);

  // Build a display-friendly list of filled variables
  const filledFields = Object.entries(formData).filter(([, val]) => val);

  // Map variable keys to labels from template schema
  const variableLabels = {};
  (demoState.templateVariables || []).forEach(v => {
    variableLabels[v.key] = v.label;
  });

  // ─── Processing Phase ──────────────────────────────────────
  if (phase === 'processing') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number">2</div>
          <span className="loan-step-text">Step 2 of 5 — Document Generation</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--doqsolv">
            ⚡ DoqSolv API
          </div>
          <div className="loan-processing">
            <div className="loan-spinner" />
            <div className="loan-processing__text">
              Generating your Loan Agreement...
            </div>
            <div className="loan-processing__subtext">
              DoqSolv is injecting your data into the PDF template
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Error Phase ───────────────────────────────────────────
  if (phase === 'error') {
    return (
      <>
        <div className="loan-step-label">
          <div className="loan-step-number" style={{ background: 'var(--loan-error)' }}>!</div>
          <span className="loan-step-text">Step 2 of 5 — Document Generation</span>
        </div>
        <div className="loan-card">
          <div className="loan-api-tag loan-api-tag--doqsolv">
            ✕ DoqSolv API — Error
          </div>
          <h2 className="loan-heading" style={{ fontSize: 22 }}>
            Generation Failed
          </h2>
          <p className="loan-subheading" style={{ marginBottom: 16 }}>
            The document generation encountered an error. Please check your configuration and try again.
          </p>
          <div className="loan-doc-preview" style={{ textAlign: 'center', padding: 16 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--loan-error)', wordBreak: 'break-all' }}>
              {errorMsg}
            </p>
          </div>
          <div style={{ marginTop: 24 }}>
            <button
              className="loan-btn loan-btn--primary"
              onClick={() => {
                hasCalledApi.current = false;
                setPhase('processing');
                setErrorMsg('');
              }}
            >
              ↻ Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─── Preview Phase (Success) ──────────────────────────────
  return (
    <>
      <div className="loan-step-label">
        <div className="loan-step-number">2</div>
        <span className="loan-step-text">Step 2 of 5 — Document Generation</span>
      </div>

      <div className="loan-card">
        <div className="loan-api-tag loan-api-tag--doqsolv">
          ✓ DoqSolv API — Complete
        </div>
        <h2 className="loan-heading" style={{ fontSize: 22 }}>
          Agreement Generated
        </h2>
        <p className="loan-subheading" style={{ marginBottom: 16 }}>
          Your Loan Agreement has been auto-populated with your details using the DoqSolv Template Engine.
        </p>

        <div className="loan-doc-preview">
          <div className="loan-doc-preview__header">
            <p className="loan-doc-preview__title">{demoState.templateName || 'LOAN AGREEMENT'}</p>
            <p className="loan-doc-preview__subtitle">
              Document ID: {demoState.documentId?.slice(0, 12)}...
            </p>
          </div>

          {/* Show all filled variable values dynamically */}
          {filledFields.map(([key, value], idx) => (
            <div
              className="loan-doc-field"
              key={key}
              style={idx === filledFields.length - 1 ? { borderBottom: 'none' } : {}}
            >
              <span className="loan-doc-field__label">
                {variableLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
              <span className="loan-doc-field__value">{value}</span>
            </div>
          ))}

          <div className="loan-doc-field" style={{ borderBottom: 'none', marginTop: 8 }}>
            <span className="loan-doc-field__label">Status</span>
            <span className="loan-doc-field__value" style={{ color: 'var(--loan-success)' }}>
              ✓ Generated & Downloaded
            </span>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <button className="loan-btn loan-btn--primary" onClick={onNext}>
            Proceed to e-Stamp Attachment →
          </button>
        </div>
      </div>
    </>
  );
};

export default Step2DoqSolv;
